/**
 * @fileoverview Connect profile-image selection and cropping to the authenticated avatar upload API. This control owns transient crop/upload state; the profile parent accepts the returned avatar through onAvatarChange. `kc-internal-frontend/src/api.js` owns HTTP session behavior and `kc-internal-frontend/src/components/Avatar/Avatar.js` owns display. Upload success and updating the parent's identity view are separate steps.
 */

import React, { useCallback, useContext, useRef, useState } from 'react';
import Button from "./AppPrimeButton";
import { Dialog } from 'primereact/dialog';
import { Slider } from 'primereact/slider';
import Cropper from 'react-easy-crop';

import ToastContext from '../../ToastContext';
import api from '../../api';
import Avatar from '../Avatar/Avatar';



function ProfileAvatarUploader({
  avatarUrl,
  name,
  onAvatarChange,
  buttonLabel = 'Change Photo',
  size = 80,
  helperText = null,
  disabled = false
}) {
  const showToast = useContext(ToastContext);
  const fileInputRef = useRef(null);
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);

  const resetCropState = useCallback(

  () => {
    setCropModalVisible(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const onFileChange = useCallback(

  (event) => {
    if (!(event.target.files && event.target.files.length > 0)) {
      return;
    }

    const file = event.target.files[0];
    const reader = new FileReader();
    reader.addEventListener('load',

    () => {
      setImageSrc(reader.result);
      setZoom(1);
      setMinZoom(1);
      setCrop({ x: 0, y: 0 });
      setCropModalVisible(true);
    });
    reader.readAsDataURL(file);
  }, []);

  const onMediaLoaded = useCallback(

  (mediaSize) => {
    const cropAreaSize = 400 * 0.8;
    const minDimension = Math.min(mediaSize.width, mediaSize.height);
    const calculatedMinZoom = cropAreaSize / minDimension;

    setMinZoom(calculatedMinZoom);
    setZoom(calculatedMinZoom);
  }, []);

  const onCropComplete = useCallback(

  (_croppedArea, nextCroppedAreaPixels) => {
    setCroppedAreaPixels(nextCroppedAreaPixels);
  }, []);

  const getCroppedImageBlob = useCallback(

  (source, pixelCrop) =>
  new Promise(

  (resolve, reject) => {
    const image = new Image();
    image.src = source;
    image.onload =

    () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      context.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('Canvas is empty'));
      }, 'image/jpeg', 0.9);
    };
    image.onerror = () => reject(new Error('Failed to load image'));
  }),
  []);

  const handleUpload = useCallback(

  async () => {
    try {
      setUploading(true);
      const croppedImageBlob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append('file', croppedImageBlob, 'avatar.jpg');

      const response = await api.post('/profile/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      onAvatarChange(response.data.url);
      showToast({ severity: 'success', summary: 'Success', detail: 'Profile picture updated successfully' });
      resetCropState();
    } catch (error) {
      console.error('Error uploading avatar:', error);
      showToast({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error?.response?.data?.error || 'Failed to upload profile picture'
      });
    } finally {
      setUploading(false);
    }
  }, [croppedAreaPixels, getCroppedImageBlob, imageSrc, onAvatarChange, resetCropState, showToast]);

  return (
    <>
            <div className="flex align-items-center gap-4">
                <Avatar
          src={avatarUrl}
          name={name}
          size={size} />

                <div className="flex-1">
                    <Button
            label={buttonLabel}
            icon="pi pi-camera"
            className="p-button-sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled} />

                    {helperText ?
          <div className="text-600 text-sm mt-2">{helperText}</div> :
          null}
                    <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={onFileChange} />

                </div>
            </div>

            <Dialog
        header="Crop Profile Picture"
        visible={cropModalVisible}
        style={{ width: '50vw' }}
        onHide={resetCropState}
        footer={
        <div>
                        <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={resetCropState}
            className="p-button-text"
            disabled={uploading} />

                        <Button
            label={uploading ? 'Uploading...' : 'Save'}
            icon={uploading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleUpload}
            disabled={uploading || !croppedAreaPixels}
            autoFocus />

                    </div>}

        modal
        dismissableMask={!uploading}>

                <div style={{ position: 'relative', width: '100%', height: 400, background: '#f3f4f6' }}>
                    {imageSrc ?
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onMediaLoaded={onMediaLoaded} /> :

          null}
                </div>
                <div className="mt-4">
                    <label htmlFor="profile-avatar-zoom" className="block text-sm font-medium mb-2">Zoom</label>
                    <Slider
            id="profile-avatar-zoom"
            value={zoom}
            min={minZoom}
            max={3}
            step={0.1}
            onChange={(event) => setZoom(event.value)} />

                </div>
            </Dialog>
        </>);

}

export default ProfileAvatarUploader;
